import type { GenericField, GenericFormType } from '@neo/blocks/forms/types'
import { v4 } from 'uuid'
import type { FileMimeType, RFileUploadResponse } from 'v2/types/api/auth'
import z from 'zod'
import { type EventCallback, EventCallbacks } from '../../components/event-callbacks'
import { MediaUpload } from '../../components/media-upload/media-upload'
import { NodevarList } from '../../components/nodevar-list'
import type { AgentPresetConversationNodeformData } from '../../types'

// SMS message-status callbacks — parity with the send_message node.
const messageStatusEventTypes = [
  { value: 'queued', label: 'Queued' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
  { value: 'undelivered', label: 'Undelivered' },
]

export const messageSpecificFields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericField<AgentPresetConversationNodeformData>[] => [
  {
    name: 'from',
    label: 'Send message from',
    zodValidation: (values) => {
      if (values.channel === 'message') {
        return z.string().min(1, 'From is required')
      }
      return z.string().optional()
    },
    componentType: 'input',
    visible: (values) => values.channel === 'message',
    onChangeListenTo: ['channel'],
    inputProps: {
      endIcon: (
        <NodevarList
          nodevars={fieldsWithDynamicNodeVars.message_from || []}
          className="pr-2"
          inputElementSelector="[name=from]"
          replaceEntireField
        />
      ),
    },
  },
  {
    name: 'to',
    label: 'To',
    zodValidation: (values) => {
      if (values.channel === 'message') {
        return z.string().min(1, 'To is required')
      }
      return z.string().optional()
    },
    componentType: 'input',
    visible: (values) => values.channel === 'message',
    onChangeListenTo: ['channel'],
    inputProps: {
      endIcon: (
        <NodevarList
          nodevars={nodeVars}
          className="pr-2"
          inputElementSelector="[name=to]"
          replaceEntireField
        />
      ),
    },
  },

  {
    name: 'message_media',
    type: 'custom',
    label: 'Upload media (optional)',
    zodValidation: z.any(),
    visible: (values) => values.channel === 'message',
    onChangeListenTo: ['channel'],
    eventDataExtractor: (data: Record<string, unknown>) => data,
    component: ({
      form,
      onChange,
    }: {
      form: GenericFormType<AgentPresetConversationNodeformData>
      onChange: (value: Record<string, unknown>[]) => void
    }) => {
      const onUrlChange = (uploadedFile: RFileUploadResponse) => {
        const mediaFiles = form.state.values.message_media || []
        const newMediaFile = {
          media_type: uploadedFile.file_type,
          name: uploadedFile.file_name,
          url: uploadedFile.file_url,
          size: uploadedFile.file_size,
          file_id: uploadedFile.file_id,
        }
        onChange([...mediaFiles, newMediaFile])
      }

      const onRemove = (fileToRemove: RFileUploadResponse) => {
        const mediaFiles = form.state.values.message_media || []
        const updatedMediaFiles = mediaFiles.filter(
          (file: { file_id?: string }) => file.file_id !== fileToRemove.file_id,
        )
        onChange(updatedMediaFiles)
      }

      let initialFiles: RFileUploadResponse[] = []
      if (form.state.values.message_media) {
        initialFiles = form.state.values.message_media.map(
          (file: {
            media_type?: string
            name?: string
            url?: string
            size?: number
            file_id?: string
          }) => {
            const fileWithId = !file.file_id ? { ...file, file_id: v4() } : file
            return {
              file_type: fileWithId.media_type as FileMimeType,
              file_name: fileWithId.name || '',
              file_url: fileWithId.url || '',
              file_size: fileWithId.size || 0,
              file_id: fileWithId.file_id || '',
            }
          },
        )
      }

      return (
        <MediaUpload
          onURLChange={onUrlChange}
          onRemove={onRemove}
          label="Upload media (optional)"
          initialFiles={initialFiles}
          multiUpload
          nodeVars={nodeVars}
        />
      )
    },
  },
]

// Message-channel status event callbacks — rendered LAST in the sidebar (after
// text/wait fields), mirroring the send_message node's field order.
export const messageEventsFields = (
  nodeVars: string[],
): GenericField<AgentPresetConversationNodeformData>[] => [
  {
    name: 'events',
    type: 'custom',
    label: '',
    zodValidation: z.any(),
    visible: (values) => values.channel === 'message',
    onChangeListenTo: ['channel'],
    eventDataExtractor: (data: unknown) => data,
    component: ({
      form,
      onChange,
    }: {
      form: GenericFormType<AgentPresetConversationNodeformData>
      onChange: (value: EventCallback[]) => void
    }) => (
      <EventCallbacks
        events={(form.state.values.events as EventCallback[]) ?? []}
        onChange={(events) => onChange(events)}
        eventTypes={messageStatusEventTypes}
        nodeVars={nodeVars}
        className="mt-4"
      />
    ),
  },
]
